import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult({ variant }) {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("polling");
    const tries = useRef(0);
    const purchaseFired = useRef(false);

    useEffect(() => {
        if (variant !== "success" || !sessionId) return;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    if (window.rdt && !purchaseFired.current) {
                        purchaseFired.current = true;
                        const value = data.lookup_key === "spot_deal_fee" ? 699 : 399;
                        window.rdt("track", "Purchase", { currency: "USD", value });
                    }
                    setState("paid");
                    return;
                }
            } catch {
                // keep polling
            }
            tries.current += 1;
            if (tries.current < 20) setTimeout(poll, 2000);
            else setState("timeout");
        };
        poll();
    }, [variant, sessionId]);

    const content = {
        polling: {
            icon: <Loader2 className="h-12 w-12 animate-spin text-[#2563EB]" aria-hidden="true" />,
            title: "Confirming your payment...",
            body: "Hold on — we're confirming with Stripe.",
            testId: "payment-polling",
        },
        paid: {
            icon: <CheckCircle2 className="h-12 w-12 text-emerald-500" aria-hidden="true" />,
            title: "Payment received.",
            body: "Your fee is paid and your allocation is active. We'll notify you by email and text as it progresses — welcome to the club.",
            testId: "payment-paid",
        },
        timeout: {
            icon: <Loader2 className="h-12 w-12 text-[#2563EB]" aria-hidden="true" />,
            title: "Still processing...",
            body: "Stripe is taking a moment. Your payment will confirm shortly — check your email.",
            testId: "payment-processing",
        },
        cancel: {
            icon: <XCircle className="h-12 w-12 text-zinc-500" aria-hidden="true" />,
            title: "Payment cancelled.",
            body: "No charge was made. Your reservation is still held — you can pay anytime by card.",
            testId: "payment-cancelled",
        },
    }[variant === "cancel" ? "cancel" : state];

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
            <div
                data-testid={content.testId}
                className="w-full max-w-md border border-white/10 bg-[#0a0a0a] p-10 text-center"
            >
                <div className="flex justify-center">{content.icon}</div>
                <h1 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight text-white">
                    {content.title}
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{content.body}</p>
                <a
                    data-testid="payment-home-link"
                    href="/"
                    className="mt-8 inline-block bg-[#2563EB] px-8 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                >
                    Back to the club
                </a>
            </div>
        </div>
    );
}
